import { postMessageToFlutterChannel } from '@sbo/app-utils';
import Cookies from 'js-cookie';

const appVersion = Cookies.get('AppVersion') ?? '';
const isFromApp = appVersion !== '';

export function useApp() {
    function loginInApp() {
        sendMessageToApp({ action: 'login' });
    }

    function updateBalanceInApp() {
        sendMessageToApp({ action: 'update_balance' });
    }

    function openDepositPageInApp() {
        sendMessageToApp({ action: 'open_deposit_page' });
    }

    function goToAcceptAnyOddsSettingsInApp() {
        sendMessageToApp({ action: 'go_accept_any_odds_setting' });
    }

    function goToAcceptAnyCashOutValueSettingsInApp() {
        sendMessageToApp({ action: 'go_accept_any_cash_out_value_setting' });
    }

    function openTncInApp(url: string) {
        sendMessageToApp({ action: 'open_tnc', url: encodeURI(url) });
    }

    function openStatisticsInApp(url: string) {
        sendMessageToApp({ action: 'open_match_stats', url: encodeURI(url) });
    }

    function goToVoucherPageInApp() {
        sendMessageToApp({ action: 'open_voucher_page' });
    }

    function goToStatementInApp() {
        sendMessageToApp({ action: 'go_to_statement' });
    }

    function goToInApp({ isFromAppPunter, fallbackTo }: { isFromAppPunter: boolean, fallbackTo: () => void }) {
        if (isFromAppPunter) {
            sendMessageToApp({ action: 'close_webview' });
        } else {
            fallbackTo();
        }
    }

    function goToHomePageInApp(reason: string) {
        sendMessageToApp({ action: 'home_page', reason });
    }

    function openVideoPipInApp(params: { matchId: number, url: string, event?: string, content?: string }) {
        sendMessageToApp({
            action: 'open_video_pip',
            matchId: params.matchId,
            url: params.url,
            ...(params.event && { event: params.event }),
            ...(params.content && { content: params.content }),
        });
    }

    // Tell the app which match detail is currently open (or that none is), so
    // the live-stream PiP "back to match" can just close the PiP when the user
    // is already on that match instead of reloading the page.
    function notifyMatchDetailOpenedInApp(matchId: number) {
        sendMessageToApp({ action: 'sports_match_detail', matchId });
    }

    function notifyMatchDetailClosedInApp() {
        sendMessageToApp({ action: 'sports_match_detail' });
    }

    // Pure GA passthrough: the app forwards event + params to FirebaseAnalytics verbatim.
    function sendGaToApp(gaEvent: { event: string, params?: Record<string, unknown> }) {
        sendMessageToApp({
            action: 'send_ga',
            event: gaEvent.event,
            ...(gaEvent.params && { params: gaEvent.params }),
        });
    }

    function setLiveStreamPipEnabledInApp(enabled: boolean) {
        sendMessageToApp({ action: 'set_live_stream_pip_enabled', content: enabled ? 'y' : 'n' });
    }

    // TCMN-12056: tell the app whether the bet slip currently covers enough of the
    // screen that the in-app PiP FAB should step out of the way.
    // New channel actions go through @sbo/app-utils so the payload contract is
    // shared with the other sites; the actions above predate it and still use the
    // local sender.
    function setBetTicketOpenInApp(enabled: boolean) {
        postMessageToFlutterChannel({ action: 'set_bet_ticket_open', content: enabled ? 'y' : 'n' });
    }

    // TCSP-8390: the App opens the page in its own in-app browser, so the player can
    // come back to Sports instead of leaving the webview. `title` is optional on the
    // App side (Jason, 2026-10-07): without it the App shows its own default title,
    // so the key is left out of the payload rather than sent as undefined.
    function openUrlWithTitleInApp(url: string, title?: string) {
        postMessageToFlutterChannel({ action: 'open_url_with_title', url, ...(title === undefined ? {} : { title }) });
    }

    function sendMessageToApp(message: object) {
        if (typeof window.flutterChannel !== 'undefined') {
            window.flutterChannel.postMessage(JSON.stringify(message));
        }
    }

    return {
        appVersion,
        isFromApp,
        loginInApp,
        updateBalanceInApp,
        openDepositPageInApp,
        goToAcceptAnyOddsSettingsInApp,
        goToAcceptAnyCashOutValueSettingsInApp,
        openTncInApp,
        openStatisticsInApp,
        goToVoucherPageInApp,
        goToStatementInApp,
        goToInApp,
        goToHomePageInApp,
        openVideoPipInApp,
        notifyMatchDetailOpenedInApp,
        notifyMatchDetailClosedInApp,
        sendGaToApp,
        setLiveStreamPipEnabledInApp,
        setBetTicketOpenInApp,
        openUrlWithTitleInApp,
    };
}
